import type { KeyboardEvent } from 'react';
import { useLingui } from '@lingui/react/macro';

import Plus from '@rover/icons/svg/plus.svg';
import { Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import Dropzone from '@rover/react-lib/src/components/utils/Dropzone';
import RoverLoading from '@rover/react-lib/src/components/utils/RoverLoading';
import { MediaType, useSelectMedia } from '@rover/rsdk/src/modules/Media';

type AddImagesTileProps = {
  onImagesAdded: (files: File[]) => void;
  isUploading?: boolean;
};

const AddImagesTile = ({ onImagesAdded, isUploading = false }: AddImagesTileProps): JSX.Element => {
  const { t } = useLingui();
  const { colors, space } = useTheme();

  const activeDropStyle = {
    color: colors.interactive_text.primary_active,
    boxShadow: `inset 0 0 0 2px ${colors.interactive_border.primary_active}`,
  };

  // `Dropzone`'s own click handler is what opens the picker, and the key event
  // lands on the element carrying it, so the press can be forwarded as a click
  // rather than reaching for the file input directly.
  const openPicker = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
    }
  };

  const { dropzoneProps } = useSelectMedia({
    multiple: true,
    type: MediaType.IMAGE,
    onSelectionSuccess: (mediaFiles) =>
      onImagesAdded(mediaFiles.map((media) => media.file as unknown as File)),
  });

  return (
    <RoverLoading isLoading={isUploading} data-testid="add-images-tile">
      <Flex
        width="100%"
        background="secondary"
        borderWidth="2px"
        borderStyle="dashed"
        borderColor={colors.border.secondary}
        borderRadius="primary"
        sx={{
          aspectRatio: '1',
          '& > div': {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: space['2x'],
            borderRadius: 'inherit',
            cursor: 'pointer',
          },
          '& > div:focus-visible': {
            outline: '5px auto -webkit-focus-ring-color',
            outlineOffset: '2px',
          },
        }}
      >
        <Dropzone
          {...dropzoneProps}
          activeStyle={activeDropStyle}
          role="button"
          tabIndex={0}
          aria-label={t`Add photos`}
          onKeyDown={openPicker}
          data-testid="add-images-dropzone"
        >
          <Plus />
        </Dropzone>
      </Flex>
    </RoverLoading>
  );
};

export default AddImagesTile;
